import React, { useCallback, useMemo, useState } from "react";

import { ResolvedContent } from "@common/components/util/ContentLoader";
import { Loading } from "@common/components/util/Loading";
import { Mapping } from "../Mapping";
import { Settings } from "../Mapping/types";
import { ApiJob, JobWarning } from "../../../common/api/v1.1/types/jobs";
import { WorkbenchTaxonomy } from "../../../common/api/v1.1/types/taxonomy";
import { convertToArrayTaxo } from "../Mapping/state/util";
import { Message } from "../../../common/components/Workbench/types";

type Props = Readonly<{
  taxonomyAdmin: boolean;
  authToken: string
  contents: ReadonlyArray<ResolvedContent>;
  noteCount: number;
  notes: React.ReactElement;
  review: boolean;
  saveContent: (xml: string) => Promise<void>;
  resolveWarning: (warning: JobWarning) => Promise<void>;
  settings: Settings;
  submitJob: () => Promise<void>;
  showMessage: (
    contents: React.ReactElement,
    title: string,
    displaySeconds?: number,
    icon?: Message["icon"],
  ) => void;  acceptAllOnSave: boolean;
  job:ApiJob
}>;

export const MappingWrapper = ({
  taxonomyAdmin,
  authToken,
  contents,
  noteCount,
  notes,
  review,
  saveContent,
  resolveWarning,
  settings,
  submitJob,
  acceptAllOnSave,
  job,
  showMessage
}: Props) => {
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const parsedContents = useMemo(() => {

    let taxonomyObj: WorkbenchTaxonomy = {
      id: job.metadata.mapping.taxonomy,
      description: '',
      type: 'mapping',
      isLegacy : false
    }

    let taxonomy = contents.find(c => c.role === "mapping-taxonomy-json")?.contents;
    // Taxonomy is ApiTaxonomy object from new api with new array format
    // Have to check the type here because it's possible the taxonomy has a datapoint
    // that is literally called "id" and would be a key in the object.  
    if (taxonomy.id && typeof(taxonomy.id) === "string") {
      taxonomyObj.isLegacy = false;
      taxonomyObj.description = taxonomy.description;
      taxonomyObj.taxonomyObject = JSON.parse(taxonomy.taxonomyJson);
    } else {
      taxonomyObj.isLegacy = true;

      // Taxonomy is simple json from job storage with old object format
      if (typeof taxonomy === "string") {
        taxonomyObj.taxonomyObject = JSON.parse(taxonomy);
      }
      taxonomyObj.taxonomyObject = convertToArrayTaxo(taxonomy);
    }

    const predictedXml = contents.find(c => c.role === "mapping-predicted-xml")?.contents;
    let xmlContent = contents.find(c => c.role === "mapping-reviewed-xml")?.contents;
    if (xmlContent === null) {
      xmlContent = predictedXml;
    }
    const meta = contents
      .find(c => c.role === "zoning-pagesmeta-json")
      ?.contents.pages.map((p: any) => p.images);
    let imageUris: Array<string> = contents
      .filter(c => c.role === "zoning-pagehr-png")
      .map(c => c.contents as string);

    if (job.parent_id) {
      imageUris = imageUris.slice(job.metadata.first_page -1, job.metadata.last_page -1);
    }

    const output = contents.find(c => c.role === "mapping-reviewed-xml");
    return { imageUris, meta, output, predictedXml, taxonomyObj, xmlContent };
  }, [contents]);
  const onReset = useCallback(async (): Promise<void> => {
    if (!parsedContents.predictedXml) {
      return;
    }
    await saveContent(parsedContents.predictedXml);
    window.location.reload();
  }, [parsedContents.predictedXml, saveContent]);
  const onSave = useCallback(
    async (newXml: string) => {
      try {
        await saveContent(newXml);
      } catch (err) {
        setSaveError(err);
        setSaving(false);
      }
    },
    [saveContent, setSaveError, setSaving],
  );
  const onComplete = useCallback(
    async (newXml: string): Promise<void> => {
      try {
        setSaving(true);
        await saveContent(newXml);
        await submitJob();
      } catch (error) {
        setSaving(false);
        setSaveError(error);
      }
    },
    [saveContent, setSaveError, setSaving, submitJob],
  );
  /*if (parsedContents.meta && parsedContents.imageUris.length !== parsedContents.meta.length) {
    const error = new Error("Page count mismatch.");
    return <Loading error={error} text="Please contact a system administrator." />;
  } else*/ if (!review && parsedContents.output && parsedContents.output.readonly) {
    const error = new Error("Output is marked as readonly. Job cannot be saved.");
    return <Loading error={error} text="Please contact a system administrator." />;
  } else if (saveError) {
    return (
      <Loading
        error={saveError}
        text="An unexpected error occurred while trying to save content."
      />
    );
  }
  return (
    <Mapping
      taxonomyAdmin={taxonomyAdmin}
      authToken={authToken}
      confidenceThreshold={0.95}
      imageUris={parsedContents.imageUris}
      noteCount={noteCount}
      notes={notes}
      warnings={job.warnings}
      onComplete={onComplete}
      onReset={onReset}
      onSave={onSave}
      showMessage={showMessage}
      resolveWarning={resolveWarning}
      pageMeta={parsedContents.meta}
      review={review}
      saving={saving}
      settings={settings}
      taxonomy={parsedContents.taxonomyObj}
      xml={parsedContents.xmlContent}
      acceptAllOnSave={acceptAllOnSave}
    />
  );
};
MappingWrapper.displayName = "MappingWrapper";
